import { useMemo } from 'react';
import { type ToolCallSummary } from '@/features/types/tools';
import { parseToolCallResult } from './tool-call-utils';

export enum ToolCallState {
  Running = 'running',
  Success = 'success',
  Error = 'error',
}

export function useToolCallParser<T extends object>(
  toolCall: ToolCallSummary,
  titleGenerator: (params: {
    toolCallState: ToolCallState;
    parsedResult: ReturnType<typeof parseToolCallResult<T>>;
  }) => string
) {
  const results = toolCall.isRunning ? undefined : toolCall.results;

  const parsedResult = useMemo(() => {
    if (toolCall.isRunning) return undefined;
    return parseToolCallResult<T>(results);
  }, [toolCall.isRunning, results]);

  const toolCallState = useMemo(() => {
    if (toolCall.isRunning) return ToolCallState.Running;
    if (parsedResult?.hasErrors) return ToolCallState.Error;
    return ToolCallState.Success;
  }, [toolCall.isRunning, parsedResult]);

  const title = titleGenerator({ toolCallState, parsedResult });

  return { parsedResult, title, toolCallState };
}
